import dynamic, { DynamicOptionsLoadingProps } from 'next/dynamic';
import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useMemo } from 'react';
import palette from '@ui/palette';
import Button from '@ui/Button';
import Typography from '@ui/Typography';
import { useStudioProjectContext } from 'pages/dashboard/studio/StudioProjectProvider';
import CircularLoader from '../../LoadingIndicators/CircularLoader';
import { useUserContext, useUserTeam } from '../../../context/user/UserContext';
import useWorkspaceMembers from '../../Workspaces/hooks/useWorkspaceMembers';
import { getWorkspaceTypeByName } from '../../Workspaces/utils';
import { WorkspaceType } from '../../Workspaces/types';
import { useUserProjectAccessQuery } from '../../Projects/ProjectAccess/operation.generated';
import { useDriveQuery } from '../../Workspaces/operation.generated';

const Loader = ({ error }: DynamicOptionsLoadingProps) => {
  if (error) {
    return (
      <Typography color="error" variant="pSm">
        Something has gone wrong...
      </Typography>
    );
  }
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
      }}
    >
      <CircularLoader />
    </div>
  );
};

const CommentTextArea = dynamic(() => import('./CommentTextArea'), {
  loading: Loader,
  ssr: false,
});

export enum CommentEditorSubmitLabel {
  Send = 'send',
  Save = 'save',
}

interface CommentEditorProps {
  text: string;
  placeholder?: string;
  submitButtonLabel?: CommentEditorSubmitLabel;
  onChange: (value: string) => void;
  onSubmit: (value: string) => Promise<void>;
  onCancel?: ({ resetEditText }: { resetEditText?: boolean }) => void;
}

const CommentEditor = ({
  text,
  submitButtonLabel = CommentEditorSubmitLabel.Save,
  placeholder,
  onChange,
  onSubmit,
  onCancel,
}: CommentEditorProps) => {
  const { projectId, driveId } = useStudioProjectContext();
  const userTeam = useUserTeam();
  const { classes } = useStyles();
  const [isMaxCharCount, setIsMaxCharCount] = React.useState(false);
  const [saveLoading, setSaveLoading] = React.useState(false);
  const [mentionUsers, setMentionUsers] = React.useState<
    {
      id: string;
      label: string;
      initials: string;
    }[]
  >([]);
  const handleSaveClick = () => {
    setSaveLoading(true);
    onSubmit(text)
      .then(() => {
        if (onCancel) onCancel({ resetEditText: false });
      })
      .catch(() => {})
      .finally(() => {
        setSaveLoading(false);
      });
  };
  const handleCancelClick = () => {
    if (onCancel) onCancel({ resetEditText: true });
  };
  const buttonLabel = React.useMemo(() => {
    if (submitButtonLabel === CommentEditorSubmitLabel.Save) {
      return saveLoading ? 'Saving...' : 'Save';
    }
    return saveLoading ? 'Sending...' : 'Send';
  }, [saveLoading, submitButtonLabel]);

  const handleChange = (value: string, characterCount: number) => {
    setIsMaxCharCount(characterCount > 300);
    onChange(value);
  };

  const { data: userData } = useUserContext();
  const { data: driveData, loading: loadingDrive } = useDriveQuery({
    variables: { inputs: { driveId } },
    skip: !driveId,
  });

  const { workspaceMembers, loading: loadingWorkspaceMembers } =
    useWorkspaceMembers({
      userId: userData?.user.id || '',
      driveId,
    });

  // Shared drive projects require project access members for mentions
  const { data: projectAccessData, loading: loadingProjectAccess } =
    useUserProjectAccessQuery({
      variables: { inputs: { projectId } },
      skip: !projectId,
    });
  const projectAccessMembers = useMemo(
    () => projectAccessData?.user.project?.projectAccess.teamMemberAccess ?? [],
    [projectAccessData]
  );
  const projectAccessMembersMapped = useMemo(
    () =>
      projectAccessMembers.map(({ teamMember }) => ({
        id: teamMember.id,
        label: teamMember.name,
        initials: teamMember.initials,
      })),
    [projectAccessMembers]
  );

  useEffect(() => {
    if (loadingDrive || loadingWorkspaceMembers || loadingProjectAccess) return;

    const driveName = driveData?.drive?.name || '';
    const workspaceType = getWorkspaceTypeByName(driveName);

    switch (workspaceType) {
      case WorkspaceType.Team:
        setMentionUsers(
          userTeam?.members.map(member => ({
            id: member.id,
            label: member.name,
            initials: member.initials,
          })) || []
        );
        break;
      case WorkspaceType.Personal: {
        setMentionUsers(projectAccessMembersMapped);
        break;
      }
      case WorkspaceType.Custom: {
        if (!driveData) {
          // For users who have another user's personal project shared with them
          setMentionUsers(projectAccessMembersMapped);
          break;
        }
        const workspaceMembersMapped = workspaceMembers.map(member => ({
          id: member.id,
          label: member.name,
          initials: member.initials,
        }));
        setMentionUsers(workspaceMembersMapped);
        break;
      }
      default:
        setMentionUsers([]);
        break;
    }
  }, [
    workspaceMembers,
    userTeam,
    driveData,
    loadingDrive,
    loadingWorkspaceMembers,
    loadingProjectAccess,
    projectAccessMembersMapped,
  ]);

  return (
    <div className={classes.container}>
      <CommentTextArea
        placeholder={placeholder}
        text={text}
        onChange={handleChange}
        mentionUsers={mentionUsers}
        disabled={saveLoading}
      />
      <div className={classes.buttonContainer}>
        {onCancel && (
          <Button
            variant="text"
            onClick={handleCancelClick}
            classes={{ text: classes.cancelButton }}
          >
            <Typography variant="h5" bold>
              Cancel
            </Typography>
          </Button>
        )}
        <Button
          variant="text"
          onClick={handleSaveClick}
          disabled={isMaxCharCount || saveLoading || !text}
          classes={{
            text: classes.saveButton,
            root: classes.disabled,
          }}
        >
          <Typography variant="h5" bold className={classes.textButtonText}>
            {buttonLabel}
          </Typography>
        </Button>
      </div>
    </div>
  );
};

const useStyles = makeStyles()(theme => ({
  container: {
    position: 'relative',
    width: '100%',
  },
  buttonContainer: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    display: 'flex',
    gap: theme.spacing(1),
  },
  saveButton: {
    padding: 0,
    color: palette.azure_300,
    '&:hover': {
      color: palette.azure_200,
    },
    textTransform: 'capitalize',
    minWidth: 'fit-content',
  },
  cancelButton: {
    padding: 0,
    color: palette.gray_75,
    '&:hover': {
      color: palette.gray_0,
    },
    textTransform: 'capitalize',
    minWidth: 'fit-content',
  },
  textButtonText: {
    color: palette.azure_300,
    '&:hover': {
      color: palette.azure_200,
    },
  },
  disabled: {
    '&.Mui-disabled': {
      color: palette.azure_300,
      opacity: 0.5,
    },
  },
}));

export default CommentEditor;
